import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#FFFFFF",
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#CCCCCC",
    paddingBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  headerLeft: {
    flexDirection: "column",
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#46237A",
  },
  subtitle: {
    fontSize: 12,
    color: "#666666",
    marginTop: 5,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
    color: "#333333",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
    paddingVertical: 3,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
  },
  label: {
    fontSize: 10,
    color: "#666666",
  },
  value: {
    fontSize: 10,
    textAlign: "right",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#000000",
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: "bold",
  },
  totalValue: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "right",
  },
  footer: {
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: "#CCCCCC",
    paddingTop: 10,
    fontSize: 8,
    color: "#999999",
    textAlign: "center",
  },
  itemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
    paddingVertical: 5,
  },
  itemName: {
    fontSize: 10,
    width: "30%",
  },
  itemQty: {
    fontSize: 10,
    width: "12%",
    textAlign: "center",
  },
  itemUom: {
    fontSize: 10,
    width: "10%",
    textAlign: "center",
  },
  itemUnitPrice: {
    fontSize: 10,
    width: "15%",
    textAlign: "right",
  },
  itemTax: {
    fontSize: 10,
    width: "10%",
    textAlign: "center",
  },
  itemTotal: {
    fontSize: 10,
    width: "18%",
    textAlign: "right",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    paddingVertical: 3,
  },
  summaryLabel: {
    fontSize: 11,
    color: "#333333",
  },
  summaryValue: {
    fontSize: 11,
    textAlign: "right",
    color: "#333333",
  },
});

const PurchaseOrderReceiptPDF = ({ po, poCode, vendor }) => {
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const purchaseAmount = po?.lines?.reduce(
    (total, item) => total + item.unit_price * item.quantity,
    0
  ) || 0;

  const gstAmount = po?.lines?.reduce(
    (total, item) => total + item.tax_amount,
    0
  ) || 0;

  const grandTotal = purchaseAmount + gstAmount;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>Purchase Order Receipt</Text>
            <Text style={styles.subtitle}>PO No: {poCode || "N/A"}</Text>
            <Text style={styles.subtitle}>
              Date: {formatDate(po?.order_date)}
            </Text>
            <Text style={styles.subtitle}>
              Status: {po?.status?.toUpperCase() || "N/A"}
            </Text>
          </View>
        </View>

        {/* Vendor Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Vendor Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Vendor Name</Text>
            <Text style={styles.value}>{vendor || "N/A"}</Text>
          </View>
          {po?.site_name && (
            <View style={styles.row}>
              <Text style={styles.label}>Site</Text>
              <Text style={styles.value}>{po.site_name}</Text>
            </View>
          )}
          {!po?.site && (
            <View style={styles.row}>
              <Text style={styles.label}>Delivery Location</Text>
              <Text style={styles.value}>Warehouse</Text>
            </View>
          )}
        </View>

        {/* Material List */}
        {po?.lines?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Materials</Text>
            <View style={styles.itemRow}>
              <Text style={[styles.itemName, { fontWeight: "bold" }]}>
                Material Name
              </Text>
              <Text style={[styles.itemQty, { fontWeight: "bold" }]}>
                Qty
              </Text>
              <Text style={[styles.itemUom, { fontWeight: "bold" }]}>
                UOM
              </Text>
              <Text style={[styles.itemUnitPrice, { fontWeight: "bold" }]}>
                Unit Price
              </Text>
              <Text style={[styles.itemTax, { fontWeight: "bold" }]}>
                GST %
              </Text>
              <Text style={[styles.itemTotal, { fontWeight: "bold" }]}>
                Total Price
              </Text>
            </View>
            {po.lines.map((item, idx) => {
              const gstPercentage = item.unit_price * item.quantity > 0
                ? ((item.tax_amount / (item.unit_price * item.quantity)) * 100).toFixed(2)
                : 0;
              
              return (
                <View style={styles.itemRow} key={idx}>
                  <Text style={styles.itemName}>
                    {item.material_name || "N/A"}
                  </Text>
                  <Text style={styles.itemQty}>{item.quantity}</Text>
                  <Text style={styles.itemUom}>{item.uom || "N/A"}</Text>
                  <Text style={styles.itemUnitPrice}>
                    Rs. {item.unit_price?.toLocaleString("en-IN") || "0"}
                  </Text>
                  <Text style={styles.itemTax}>{gstPercentage}%</Text>
                  <Text style={styles.itemTotal}>
                    Rs. {item.total_price?.toLocaleString("en-IN") || "0"}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Payment Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Summary</Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Purchase Amount</Text>
            <Text style={styles.summaryValue}>
              Rs. {purchaseAmount.toLocaleString("en-IN")}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>GST Amount</Text>
            <Text style={styles.summaryValue}>
              Rs. {gstAmount.toLocaleString("en-IN")}
            </Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Grand Total</Text>
            <Text style={styles.totalValue}>
              Rs. {grandTotal.toLocaleString("en-IN")}
            </Text>
          </View>
        </View>

        {/* Payment Method */}
        {po?.payment_method && (
          <View style={styles.section}>
            <View style={styles.row}>
              <Text style={styles.label}>Payment Method</Text>
              <Text style={styles.value}>{po.payment_method}</Text>
            </View>
          </View>
        )}

        <View style={styles.footer}>
          <Text>
            This is a computer-generated receipt and does not require signature.
          </Text>
          <Text>For any queries, please contact support.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PurchaseOrderReceiptPDF;